import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '../base/utils';
import { useMonkeysComponentAttributes } from '../../provider/context';
import { getOverlaySurfaceClassName } from './internal/overlaySurface';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;

export interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  /** Optional portal container, useful when a popover is opened inside a dialog. */
  container?: HTMLElement | null;
}

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(function PopoverContent(
  { align = 'center', sideOffset = 4, container, className, style, ...props },
  ref,
) {
  const componentAttributes = useMonkeysComponentAttributes();
  const fallbackContainer =
    typeof document !== 'undefined'
      ? (() => {
          const active = document.activeElement as HTMLElement | null;
          const activeDialog = active?.closest('[data-monkeys-dialog-content]');
          if (activeDialog) return activeDialog as HTMLElement;
          const openDialogs = Array.from(
            document.querySelectorAll<HTMLElement>(
              '[data-monkeys-dialog-content][data-state="open"]',
            ),
          );
          return openDialogs.at(-1);
        })()
      : undefined;

  return (
    <PopoverPrimitive.Portal container={container ?? fallbackContainer}>
      <PopoverPrimitive.Content
        {...componentAttributes}
        {...props}
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        data-monkeys-component="popover"
        data-slot="content"
        data-surface="menu"
        className={cn(
          'z-50 w-72 border p-4 outline-none',
          getOverlaySurfaceClassName('menu'),
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        style={{ pointerEvents: 'auto', ...style }}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
